/**
 * `field-applicability-by-type` — per-field fill rate by contract type ·
 * CLM-133 (FR-30 amendment, D-131-1).
 *
 * One read-only `GET /v1/admin/field-applicability-by-type`, behind the same
 * Admin **Extraction accuracy** surface as `field-accuracy` (CLM-107). For
 * each governed field and each ratified contract type (MSA, NDA, SOW), it
 * answers: of the contracts of that type we've actually measured, what share
 * have a value for this field?
 *
 * ## What it deliberately does not answer
 *
 * **Whether a field "belongs" to a type.** There is no applies/doesn't-apply
 * flag, and nothing here changes what extraction asks for or what the UI
 * shows per type (`phase4-specs/CLM-133-field-applicability-by-contract-type-requirements.md`
 * §4, §6). A high or low rate is a number for a person to read — Gurminder,
 * Sales — same posture as `field-accuracy`'s own "no threshold, no pass/fail."
 *
 * ## `measured_contracts` vs. `total_contracts` (requirements §5)
 *
 * A contract with no `field_verification` row for a field — not yet
 * extracted, or held at `ExtractionFailed` — is excluded from the count
 * entirely, never treated as null. `total_contracts` is every contract of
 * that type in scope; `measured_contracts` is how many of those actually
 * have a row for this field. The page must show both, never `populated_rate`
 * alone, so a type with few measured contracts doesn't read as misleadingly
 * clean.
 *
 * ## Scope (CLAUDE.md rules 9, 10)
 *
 * Restated with `toScopeSqlPredicate` on `vw_contract_scope.effective_contract_key`
 * inside both aggregates, the same two-CTE pattern `field-accuracy` uses.
 * `scope_kind` says which — a `practices` admin is looking at part of the
 * corpus.
 *
 * ## Gate
 *
 * `admin:configure` — the same gate as `field-accuracy`, since this is the
 * same page.
 */
import { z } from 'zod';
import { FieldNameSchema } from '../domain/verification.js';
import { IsoTimestampUtcSchema } from '../primitives.js';
import { CONTRACT_TYPES_RATIFIED } from '../vocabulary.js';
/** No parameters. `.strict()`, so an attempt at a `scope` or `contract_type` filter is a 400, never ignored. */
export const FieldApplicabilityByTypeRequestSchema = z.object({}).strict();
/**
 * One `(field_name, contract_type)` cell. `populated_rate` is `null` exactly
 * when `measured_contracts` is 0 — never 0, which would read as "never
 * populated" rather than "nothing measured yet".
 */
export const FieldApplicabilityByTypeRowSchema = z
    .object({
    field_name: FieldNameSchema,
    contract_type: z.enum(CONTRACT_TYPES_RATIFIED),
    /** Every contract of this type in the caller's scope, regardless of extraction state. */
    total_contracts: z.number().int().min(0),
    /** Of those, how many have a `field_verification` row for this field — the count this rate rests on. */
    measured_contracts: z.number().int().min(0),
    /** Of the measured contracts, how many have a non-null `extracted_value`. */
    populated: z.number().int().min(0),
    populated_rate: z.number().min(0).max(1).nullable(),
    /** `orchestrator/fields.ts`'s `HIGH_STAKES_FIELDS` — the same set `field-accuracy` badges. */
    is_high_stakes: z.boolean(),
})
    .strict()
    .superRefine((row, ctx) => {
    if (row.measured_contracts > row.total_contracts) {
        ctx.addIssue({
            code: 'custom',
            path: ['measured_contracts'],
            message: 'measured_contracts cannot exceed total_contracts',
        });
    }
    if (row.populated > row.measured_contracts) {
        ctx.addIssue({ code: 'custom', path: ['populated'], message: 'populated cannot exceed measured_contracts' });
    }
    if ((row.populated_rate === null) !== (row.measured_contracts === 0)) {
        ctx.addIssue({
            code: 'custom',
            path: ['populated_rate'],
            message: 'populated_rate is null exactly when measured_contracts is 0 — never 0 for an unmeasured cell',
        });
    }
});
export const FieldApplicabilityByTypeResponseSchema = z
    .object({
    generated_utc: IsoTimestampUtcSchema,
    /** The caller's scope shape, never the practice list (field-accuracy's R-107-5 pattern, reused). */
    scope_kind: z.enum(['all', 'practices']),
    /** One row per `(field_name, contract_type)` present in scope; a type with zero contracts in scope contributes no rows. */
    rows: z.array(FieldApplicabilityByTypeRowSchema),
})
    .strict();
//# sourceMappingURL=field-applicability-by-type.js.map